import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { DateTimezonePipe } from 'src/app/shared/pipes/date-timezone.pipe';
import { API_URL_COMPANIES, BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { environment } from 'src/environments/environment';
import { Scenario } from './models';
import { ScenarioTemplate } from './models/scenario-template.model';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';

@Injectable({
  providedIn: 'root',
})
export class ScenariosService {
  flavor: 'quick-replies' | 'campaign-scenarios' = 'quick-replies';

  get flavorQueryParams(): string {
    return `flavor=${this.flavor}`;
  }

  constructor(
    private httpClient: HttpClient,
    private alcmeonContextService: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService,
    private dateTimezonePipe: DateTimezonePipe,
    private sitesService: SitesService,
    private basicService: BasicService,
    private searchBarService: SearchBarService,
    @Inject('$state') private $state: any
  ) {}

  retrieveQuickReplies = (): Observable<HttpResponse<Scenario[]>> => {
    const body = this.searchBarService.buildCriteria();
    body['flavor'] = this.flavor;
    return this.httpClient
      .post<Scenario[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/scenarios`, body, {
        observe: 'response',
      })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  };

  findAll(): Observable<Scenario[]> {
    return this.httpClient
      .get<Scenario[]>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/scenarios?${this.flavorQueryParams}`
      )
      .pipe(
        map(quickReplies => quickReplies.map(this.formatScenario)),
        this.errorHandlingService.catchAlertAndThrowHttpError()
      );
  }

  findMarketingAndCareScenarios(): Observable<Scenario[]> {
    return forkJoin({
      quickReplies: this.httpClient
        .get<Scenario[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/scenarios?flavor=quick-replies`)
        .pipe(catchError(() => of([]))),
      marketing: this.httpClient
        .get<Scenario[]>(
          `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/scenarios?flavor=campaign-scenarios`
        )
        .pipe(catchError(() => of([]))),
    }).pipe(
      map(({ quickReplies = [], marketing = [] }) =>
        [].concat(quickReplies).concat(marketing).map(this.formatScenario)
      ),
      this.errorHandlingService.catchAlertAndThrowHttpError()
    );
  }

  private formatScenario = (scenario: Scenario) => {
    const { creation_date, last_modification_date, created_by_email, last_modified_by_email, flavor } = scenario;
    const scenarioState = this.$state.href(`auth.alcmeon.fuse.parameters.account.${flavor}-details`, {
      siteId: scenario.site_id,
      scenarioId: scenario.id,
    });
    const administratorLabel = this.basicService.translate('admin/users/is_admin');
    const notApplicableLabel = this.basicService.translate('parameters/scenarios/not_applicable');

    return {
      ...scenario,
      url: `${environment.frontendURLPrefix}${scenarioState}`,
      createDateLabel: creation_date ? this.dateTimezonePipe.transform(creation_date, 'short') : notApplicableLabel,
      modifyDateLabel: last_modification_date
        ? this.dateTimezonePipe.transform(last_modification_date, 'short')
        : notApplicableLabel,
      createdByEmail: created_by_email || administratorLabel,
      lastModifiedByEmail: last_modified_by_email || administratorLabel,
      site: this.sitesService.sitesV2Map.get(+scenario.site_id),
    };
  };

  /**
   * Allows to create the given scenario.
   * @param param0 the original scenario.
   * @param param1 the new scenario name and account.
   * @returns
   */
  createScenario({
    selectedSiteId,
    scenarioName,
    selectedScenarioTemplate,
  }: {
    selectedSiteId: number;
    scenarioName: string;
    selectedScenarioTemplate: number;
  }) {
    const body = {
      site_id: selectedSiteId,
      name: scenarioName || '',
      business_hours_activated: 0,
      flavor: this.flavor,
      scenario_template_id: selectedScenarioTemplate,
    };

    return this.httpClient
      .post<any>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${selectedSiteId}/scenarios?${this.flavorQueryParams}`,
        body
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * Allows to duplicate the given scenario from its `site_id` and its `id` to a new scenario.
   * @param param0 the original scenario.
   * @param param1 the new scenario name and account.
   * @returns
   */
  copyScenario({ site_id, id }, { selectedSiteId, scenarioName }) {
    const body = {
      destination_site_id: selectedSiteId,
      scenario_name: scenarioName,
    };

    return this.httpClient
      .post<{ new_scenario_id: number }>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${site_id}/scenarios/${id}/copies?${this.flavorQueryParams}`,
        body
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  deleteScenario({ id, site_id }: Scenario): Observable<number> {
    return this.httpClient
      .delete<number>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${site_id}/scenarios/${id}?${this.flavorQueryParams}`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  editScenario(scenario: Scenario) {
    return this.$state.go(`auth.alcmeon.fuse.parameters.account.${this.flavor}-details`, {
      siteId: scenario.site_id,
      scenarioId: scenario.id,
    });
  }

  getScenarioTemplates(
    type: number,
    scenarioFlavor: 'quick-replies' | 'campaign-scenarios'
  ): Observable<ScenarioTemplate[]> {
    let usage = '';

    if (scenarioFlavor === 'quick-replies') {
      usage = 'care';
    } else if (scenarioFlavor === 'campaign-scenarios') {
      usage = 'marketing';
    } else {
      return of([]);
    }
    return this.httpClient
      .post<ScenarioTemplate[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/scenario-template`, {
        type,
        usage,
      })
      .pipe(
        map(result =>
          result.map(item => ({
            ...item,
            previewURL: item.preview ? `url('${item.preview}')` : undefined,
          }))
        ),
        catchError(() => of([]))
      );
  }
}
